// ─────────────────────────────────────────────────────────────
// Single source of truth for CRM access control (roles + permissions)
// Used by AdminLayout (sidebar), ProtectedRoute (route guard) and Users (admin UI).
// Keep this in sync with the backend defaults in server.py (role_default_permissions).
// ─────────────────────────────────────────────────────────────

// Every grantable section: key + label + description + category + the route it controls.
export const PERMISSION_SECTIONS = [
  // Website
  { key: 'dashboard', label: 'Dashboard', description: 'Main admin dashboard', category: 'Website', route: '/admin' },
  { key: 'pages', label: 'Pages', description: 'Manage website pages', category: 'Website', route: '/admin/pages' },
  { key: 'blog', label: 'SEO Blog Engine', description: 'Create and manage blog posts', category: 'Website', route: '/admin/blog' },
  { key: 'blog_analytics', label: 'Blog Analytics', description: 'Blog performance analytics', category: 'Website', route: '/admin/blog/analytics' },
  { key: 'seo_pages', label: 'SEO Pages', description: 'Programmatic SEO landing pages', category: 'Website', route: '/admin/seo-pages' },

  // CRM & Sales (core)
  { key: 'sales_dashboard', label: 'Today (My Dashboard)', description: 'Daily tasks and KPIs', category: 'CRM & Sales', route: '/admin/crm/my-dashboard' },
  { key: 'leads', label: 'Pipeline', description: 'Lead management and conversion', category: 'CRM & Sales', route: '/admin/crm/leads' },
  { key: 'members', label: 'Members', description: 'Member management and profiles', category: 'CRM & Sales', route: '/admin/crm/members' },
  { key: 'followups', label: 'Follow-ups', description: 'Scheduled follow-up tasks', category: 'CRM & Sales', route: '/admin/crm/followups' },
  { key: 'activity_center', label: 'Activity Center', description: 'Team activity feed', category: 'CRM & Sales', route: '/admin/crm/activity' },
  { key: 'pt_schedule', label: 'PT Schedule', description: 'Personal training calendar', category: 'CRM & Sales', route: '/admin/crm/pt-schedule' },

  // CRM Operations
  { key: 'sales_overview', label: 'Sales Overview', description: 'Sales performance overview', category: 'CRM Operations', route: '/admin/crm' },
  { key: 'campaigns', label: 'Campaigns', description: 'Marketing campaigns', category: 'CRM Operations', route: '/admin/crm/campaigns' },
  { key: 'lead_database', label: 'Lead Database', description: 'Historical lead database & marketing view (Manager/Admin)', category: 'CRM Operations', route: '/admin/crm/lead-database' },
  { key: 'products', label: 'Products & Pricing', description: 'Membership and product management', category: 'CRM Operations', route: '/admin/crm/products' },
  { key: 'inventory', label: 'Inventory', description: 'Stock and inventory', category: 'CRM Operations', route: '/admin/inventory' },
  { key: 'events', label: 'Events & Programmes', description: 'Events and programmes', category: 'CRM Operations', route: '/admin/events' },
  { key: 'feedback', label: 'Customer Feedback', description: 'View and manage feedback', category: 'CRM Operations', route: '/admin/feedback' },
  { key: 'icebath', label: 'Ice Bath Booking', description: 'Ice bath session management', category: 'CRM Operations', route: '/admin/icebath' },
  { key: 'snake_pit', label: 'Snake Pit', description: 'Snake Pit management', category: 'CRM Operations', route: '/admin/snake-pit' },
  { key: 'fight_calendar', label: 'Fight Calendar', description: 'Public India-wide combat sports events calendar', category: 'CRM Operations', route: '/admin/fight-calendar' },
  { key: 'renewal_settings', label: 'Renewal Engine', description: 'Self-renewal engine settings', category: 'CRM Operations', route: '/admin/renewal-settings' },
  { key: 'outstanding_payments', label: 'Outstanding Payments', description: 'Pending payment tracking', category: 'CRM Operations', route: '/admin/crm/outstanding-payments' },
  { key: 'phone_audit', label: 'Phone Audit', description: 'Phone number audit', category: 'CRM Operations', route: '/admin/phone-audit' },
  { key: 'playbooks', label: 'Playbooks', description: 'Operational playbooks', category: 'CRM Operations', route: '/admin/crm/playbooks' },
  { key: 'training_decks', label: 'Training Decks', description: 'Coach & fighter training decks', category: 'CRM Operations', route: '/admin/crm/training-decks' },
  { key: 'links_portals', label: 'Links & Portals', description: 'Shared links and portals', category: 'CRM Operations', route: '/admin/crm/links' },

  // Communication
  { key: 'whatsapp', label: 'WhatsApp', description: 'WhatsApp messaging & automations', category: 'Communication', route: '/admin/crm/whatsapp' },
  { key: 'call_analytics', label: 'Call Analytics', description: 'Call tracking and analytics', category: 'Communication', route: '/admin/crm/calls' },
  { key: 'email_automations', label: 'Email Automations', description: 'Automated email campaigns', category: 'Communication', route: '/admin/settings/email' },

  // Analytics
  { key: 'operator_intelligence', label: 'Operator Intelligence', description: 'Business constraint analysis', category: 'Analytics', route: '/admin/operator-intelligence' },
  { key: 'business_analytics', label: 'Sales & Analytics', description: 'Revenue and purchase analytics', category: 'Analytics', route: '/admin/purchase-analytics' },
  { key: 'website_analytics', label: 'Website & Pages', description: 'Website visitor analytics', category: 'Analytics', route: '/admin/website-analytics' },
  { key: 'social_engagement', label: 'Ambassador Tracker', description: 'Social media ambassadors', category: 'Analytics', route: '/admin/social-engagement' },
  { key: 'data_health', label: 'Data Health Check', description: 'Data integrity monitoring', category: 'Analytics', route: '/admin/data-health' },

  // HR & Operations
  { key: 'employees', label: 'Team Members', description: 'Staff management', category: 'HR & Operations', route: '/admin/crm/employees' },
  { key: 'team_calendar', label: 'Team Calendar', description: 'Team schedule overview', category: 'HR & Operations', route: '/admin/hr/calendar' },
  { key: 'hr_module', label: 'Leave & Documents', description: 'Leave requests and HR documents', category: 'HR & Operations', route: '/admin/hr' },
  { key: 'team_leave_requests', label: 'Team Leave Requests', description: 'Approve or reject team leave', category: 'HR & Operations', route: '/admin/leave/team-requests' },
  { key: 'work_attendance', label: 'Work Attendance', description: 'Geo check-in attendance', category: 'HR & Operations', route: '/admin/crm/attendance' },
  { key: 'attendance', label: 'Attendance Comparison', description: 'Attendance tracking & comparison', category: 'HR & Operations', route: '/admin/hr/attendance-comparison' },

  // Financials
  { key: 'payroll', label: 'Payroll', description: 'Salary runs and incentives', category: 'Financials', route: '/admin/payroll' },
  { key: 'tax_compliance', label: 'Tax & Compliance', description: 'Tax dues and GST tracking', category: 'Financials', route: '/admin/tax-payroll' },
  { key: 'financials', label: 'P&L Overview', description: 'Profit & Loss and expense tracking', category: 'Financials', route: '/admin/financials' },

  // Settings
  { key: 'company_settings', label: 'Company Settings', description: 'Company info and branding', category: 'Settings', route: '/admin/settings/company' },
  { key: 'site_settings', label: 'Site Settings', description: 'Website configuration', category: 'Settings', route: '/admin/settings' },
  { key: 'user_management', label: 'User Management', description: 'Add and manage CRM users', category: 'Settings', route: '/admin/users' },

  // MacroBeast (meal-plan kitchen & café — kept separate from gym finances)
  { key: 'macrobeast_dashboard', label: 'MacroBeast Dashboard', description: 'MacroBeast profitability overview', category: 'MacroBeast', route: '/admin/macrobeast' },
  { key: 'macrobeast_subscriptions', label: 'MacroBeast Meal Plans', description: 'Meal-plan subscriptions', category: 'MacroBeast', route: '/admin/macrobeast/subscriptions' },
  { key: 'macrobeast_pos', label: 'MacroBeast Café POS', description: 'Café point-of-sale and products', category: 'MacroBeast', route: '/admin/macrobeast/pos' },
  { key: 'macrobeast_expenses', label: 'MacroBeast Expenses', description: 'Kitchen & café expenses', category: 'MacroBeast', route: '/admin/macrobeast/expenses' },
];

// Ordered list of categories for the permissions modal.
export const PERMISSION_CATEGORIES = ['Website', 'CRM & Sales', 'CRM Operations', 'Communication', 'Analytics', 'HR & Operations', 'Financials', 'MacroBeast', 'Settings'];

// Roles that always have full access (permissions are ignored for them).
export const FULL_ACCESS_ROLES = ['owner', 'super_admin', 'admin', 'manager'];

// Roles whose access is governed by the permission toggles.
export const PERMISSION_DRIVEN_ROLES = ['sales', 'coach', 'hr', 'media', 'chef', 'assistant'];

// Selectable roles in the "add user" dropdown.
export const ROLE_OPTIONS = [
  { value: 'sales', label: 'Sales' },
  { value: 'coach', label: 'Coach' },
  { value: 'media', label: 'Media' },
  { value: 'hr', label: 'HR' },
  { value: 'chef', label: 'MacroBeast Chef' },
  { value: 'assistant', label: 'MacroBeast Assistant' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
  { value: 'super_admin', label: 'Super Admin' },
];

// Sensible starting permissions per permission-driven role (used as a fallback
// when a user has no explicit permissions set yet). Admin can fine-tune afterwards.
export const ROLE_DEFAULTS = {
  sales: ['sales_dashboard', 'leads', 'members', 'followups', 'pt_schedule'],
  coach: ['sales_dashboard', 'members', 'followups', 'pt_schedule', 'training_decks', 'playbooks'],
  hr: ['dashboard', 'employees', 'team_calendar', 'hr_module', 'team_leave_requests', 'work_attendance', 'attendance'],
  media: ['training_decks', 'playbooks', 'links_portals', 'pages', 'blog', 'blog_analytics', 'seo_pages', 'social_engagement', 'fight_calendar'],
  chef: ['macrobeast_dashboard', 'macrobeast_subscriptions', 'macrobeast_pos', 'macrobeast_expenses'],
  assistant: ['macrobeast_pos', 'macrobeast_subscriptions'],
};

export const ALL_PERMISSION_KEYS = PERMISSION_SECTIONS.map((s) => s.key);

const PERMISSION_ROUTE_MAP = PERMISSION_SECTIONS.reduce((acc, s) => { acc[s.key] = s.route; return acc; }, {});
export { PERMISSION_ROUTE_MAP };

// Routes that must match EXACTLY (not by prefix) so children aren't gated by them.
const EXACT_ONLY = new Set(['/admin', '/admin/crm']);

// Routes restricted to top admins regardless of permissions.
export const SUPER_ADMIN_ONLY_ROUTES = ['/admin/users'];

// Resolve the effective permission map for a user.
export const getEffectivePermissions = (user) => {
  const role = user?.role;
  if (FULL_ACCESS_ROLES.includes(role)) {
    return ALL_PERMISSION_KEYS.reduce((acc, k) => { acc[k] = true; return acc; }, {});
  }
  // Role defaults are the baseline every user of this role should always have.
  // Stored per-user permissions layer on top (so newly-added default perms auto-apply).
  const base = (ROLE_DEFAULTS[role] || []).reduce((acc, k) => { acc[k] = true; return acc; }, {});
  const stored = user?.permissions;
  if (stored && Object.keys(stored).length > 0) return { ...base, ...stored };
  return base;
};

// Can this user access a given path?
export const canAccessPath = (user, path) => {
  if (!user) return false;
  if (SUPER_ADMIN_ONLY_ROUTES.some((r) => path === r || path.startsWith(r + '/'))) {
    return ['owner', 'super_admin', 'admin'].includes(user.role);
  }
  if (FULL_ACCESS_ROLES.includes(user.role)) return true;
  const eff = getEffectivePermissions(user);
  // "Revenue to Chase" is open to anyone with Members access
  if (path === '/admin/crm/outstanding-payments' || path.startsWith('/admin/crm/outstanding-payments/')) {
    return eff.members === true;
  }
  const sorted = Object.entries(PERMISSION_ROUTE_MAP).sort((a, b) => b[1].length - a[1].length);
  for (const [permKey, routePath] of sorted) {
    const matches = path === routePath || (path.startsWith(routePath + '/') && !EXACT_ONLY.has(routePath));
    if (matches) return eff[permKey] === true;
  }
  return true; // unmapped routes (e.g. My Portal, My Leaves, Help) are always allowed
};

// Preferred order for choosing a user's landing page.
const LANDING_ORDER = [
  '/admin/crm/my-dashboard', '/admin/crm/leads', '/admin/crm/members', '/admin/crm/followups',
  '/admin/crm/employees', '/admin/crm/training-decks', '/admin/crm/playbooks',
  '/admin/pages', '/admin/blog', '/admin/social-engagement', '/admin',
];

// The first page a user is allowed to see — used as the post-login + no-access landing.
// Always falls back to "My Leaves", which every logged-in user can open.
export const firstAccessiblePath = (user) => {
  if (FULL_ACCESS_ROLES.includes(user?.role)) return '/admin';
  for (const path of LANDING_ORDER) {
    if (canAccessPath(user, path)) return path;
  }
  return '/admin/leave/my-leaves';
};
